// AYA Multiservice — page principale du parcours (config → paiement → récap → traitement → résultat).
import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, HelpCircle, History, X } from "lucide-react";
import * as api from "./multiserviceApi";
import ConfigStep from "./ConfigStep";
import PaymentStep from "./PaymentStep";
import RecapStep from "./RecapStep";
import ProcessingStep from "./ProcessingStep";
import ResultStep from "./ResultStep";
import { InitialsAvatar, BrandLogo } from "./BrandLogo";
import "./multiservice.css";

const DRAFT_KEY = "aya_ms_draft";
const PHONE_RE = /^0[157]\d{8}$/;
const cleanPhone = (p) => String(p || "").replace(/[\s.\-]/g, "").replace(/^\+?225/, "");
const DEFAULT_DRAFT = { operatorId: "orange", rechargeType: "credit", phone: "", contactId: null, offerId: null, amount: null, paymentMethodId: null };

export default function MultiservicePage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [catalog, setCatalog] = useState(null);
  const [step, setStep] = useState("config");
  const [draft, setDraft] = useState(() => {
    try { return { ...DEFAULT_DRAFT, ...(JSON.parse(sessionStorage.getItem(DRAFT_KEY)) || {}) }; } catch { return { ...DEFAULT_DRAFT }; }
  });
  const [offers, setOffers] = useState([]);
  const [offersLoading, setOffersLoading] = useState(false);
  const [quote, setQuote] = useState(null);
  const [order, setOrder] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [sheet, setSheet] = useState(null); // null | "all" | "favorites" | "recent" | "help"
  const [recentOrders, setRecentOrders] = useState([]);
  const clientRef = useRef(null);
  const pollRef = useRef(null);

  const patch = useCallback((p) => setDraft((d) => ({ ...d, ...p })), []);
  useEffect(() => { sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draft)); }, [draft]);

  useEffect(() => {
    api.getCatalog().then((j) => { if (j.ok) setCatalog(j); else toast.error("Catalogue indisponible."); }).catch(() => toast.error("Catalogue indisponible."));
    const op = params.get("op"); const type = params.get("type");
    const p = {};
    if (op && ["orange", "mtn", "moov"].includes(op)) p.operatorId = op;
    if (type && ["credit", "internet", "pass"].includes(type)) p.rechargeType = type;
    if (Object.keys(p).length) patch({ ...p, offerId: null });
    if (params.get("gift") === "1") setSheet("all");
  }, []); // eslint-disable-line

  useEffect(() => {
    if (draft.rechargeType === "credit") { setOffers([]); return; }
    setOffersLoading(true);
    api.getOffers(draft.operatorId, draft.rechargeType)
      .then((j) => setOffers(j.ok ? j.offers : []))
      .catch(() => setOffers([]))
      .finally(() => setOffersLoading(false));
  }, [draft.operatorId, draft.rechargeType]);

  const normPhone = cleanPhone(draft.phone);
  const phoneError = draft.phone && !PHONE_RE.test(normPhone) ? "Numéro ivoirien invalide (10 chiffres, commençant par 01, 05 ou 07)." : null;
  const selectedOffer = offers.find((o) => o.id === draft.offerId) || null;
  const serviceAmount = draft.rechargeType === "credit" ? draft.amount : selectedOffer?.amount || null;
  const canContinue = Boolean(PHONE_RE.test(normPhone) && serviceAmount && draft.operatorId);
  const selPm = (catalog?.payment_methods || []).find((m) => m.id === draft.paymentMethodId);
  const estimatedTotal = (serviceAmount || 0) + (selPm?.fees || 0);

  const quotePayload = () => ({
    operator_id: draft.operatorId, recharge_type: draft.rechargeType, phone: normPhone,
    ...(draft.rechargeType === "credit" ? { amount: draft.amount } : { offer_id: draft.offerId }),
  });

  const goPayment = () => { setStep("payment"); window.scrollTo(0, 0); };

  const goRecap = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const j = await api.createQuote({ ...quotePayload(), payment_method_id: draft.paymentMethodId });
      if (!j.ok) { toast.error(j.error === "insufficient_wallet_balance" ? "Solde AYA Wallet simulé insuffisant." : "Vérifiez votre sélection."); return; }
      setQuote(j.quote);
      clientRef.current = api.newClientRef();
      setStep("recap"); window.scrollTo(0, 0);
    } catch { toast.error("Erreur réseau."); } finally { setSubmitting(false); }
  };

  const startPolling = useCallback((orderId) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const j = await api.getOrderStatus(orderId);
        if (!j.ok) return;
        setOrder(j.order);
        if (["success", "failed", "pending", "unknown"].includes(j.order.status)) {
          clearInterval(pollRef.current);
          setStep("result"); window.scrollTo(0, 0);
        }
      } catch { /* réseau : on réessaie au tick suivant */ }
    }, 700);
  }, []);
  useEffect(() => () => clearInterval(pollRef.current), []);

  const confirm = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const c = await api.createOrder({ ...quotePayload(), payment_method_id: draft.paymentMethodId, client_ref: clientRef.current });
      if (!c.ok) { toast.error("Impossible de créer la commande simulée."); return; }
      const f = await api.confirmOrder(c.order.id);
      if (!f.ok) { toast.error("Confirmation impossible."); return; }
      setOrder(f.order);
      setStep("processing"); window.scrollTo(0, 0);
      startPolling(c.order.id);
    } catch { toast.error("Erreur réseau."); } finally { setSubmitting(false); }
  };

  const retry = async () => {
    if (retrying || !order) return;
    setRetrying(true);
    try {
      const c = await api.createOrder({ ...quotePayload(), payment_method_id: draft.paymentMethodId, client_ref: api.newClientRef(), retry_of: order.id });
      if (!c.ok) { toast.error("Nouvelle tentative impossible."); return; }
      const f = await api.confirmOrder(c.order.id);
      if (!f.ok) { toast.error("Confirmation impossible."); return; }
      setOrder(f.order); setStep("processing"); window.scrollTo(0, 0); startPolling(c.order.id);
    } catch { toast.error("Erreur réseau."); } finally { setRetrying(false); }
  };

  const newRecharge = () => { setOrder(null); setQuote(null); patch({ offerId: null, amount: null }); setStep("config"); window.scrollTo(0, 0); };

  const back = () => {
    if (step === "payment") return setStep("config");
    if (step === "recap") return setStep("payment");
    if (step === "processing") { toast.info("La simulation continue — retrouvez-la dans l'historique."); return navigate("/search"); }
    if (step === "result") return newRecharge();
    navigate(-1);
  };

  const openContacts = async (mode) => {
    if (mode === "recent") {
      try { const j = await api.getOrderHistory(); setRecentOrders(j.ok ? j.orders.slice(0, 6) : []); } catch { setRecentOrders([]); }
    }
    setSheet(mode);
  };

  const pickContact = (c) => { patch({ contactId: c.id, phone: c.phone }); setSheet(null); };
  const pickRecent = (o) => {
    patch({ operatorId: o.operator_id, rechargeType: o.recharge_type, phone: o.beneficiary_phone, contactId: null, offerId: o.offer_id, amount: o.recharge_type === "credit" ? o.service_amount : null });
    setSheet(null); setStep("config");
  };

  const sheetContacts = (catalog?.contacts || []).filter((c) => (sheet === "favorites" ? c.favorite : true));

  return (
    <div className="ms-page" data-testid="multiservice-page">
      <header className="ms-header">
        <button className="ms-back" data-testid="ms-back-btn" aria-label="Retour" onClick={back}><ArrowLeft size={18} /></button>
        <div className="ms-htitles">
          <h1 className="ms-htitle">AYA Multiservice</h1>
          <p className="ms-hsub">Rechargez en quelques secondes</p>
        </div>
        <button className="ms-hbtn" data-testid="ms-help-btn" onClick={() => setSheet("help")}><HelpCircle size={14} /> Aide</button>
        <button className="ms-hbtn" data-testid="ms-history-btn" onClick={() => navigate("/multiservice/historique")}><History size={14} /> Historique</button>
      </header>

      {step === "config" && (
        <ConfigStep catalog={catalog} offers={offers} offersLoading={offersLoading} draft={draft} patch={patch}
          phoneError={phoneError} canContinue={canContinue} serviceAmount={serviceAmount} onContinue={goPayment} openContacts={openContacts} />
      )}
      {step === "payment" && (
        <PaymentStep catalog={catalog} draft={draft} patch={patch} total={estimatedTotal} onContinue={goRecap} submitting={submitting} />
      )}
      {step === "recap" && (
        <RecapStep catalog={catalog} quote={quote} submitting={submitting}
          onEditConfig={() => setStep("config")} onEditPayment={() => setStep("payment")} onConfirm={confirm} />
      )}
      {step === "processing" && <ProcessingStep catalog={catalog} order={order} />}
      {step === "result" && (
        <ResultStep catalog={catalog} order={order} retrying={retrying} onNewRecharge={newRecharge} onRetry={retry}
          onBackToConfig={() => setStep("config")} onHistory={() => navigate("/multiservice/historique")} />
      )}

      {sheet && sheet !== "help" && (
        <div className="ms-sheet-mask" data-testid="ms-contact-sheet" onClick={() => setSheet(null)}>
          <div className="ms-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="ms-sheet-head">
              <b>{sheet === "favorites" ? "Mes numéros favoris" : sheet === "recent" ? "Recharges récentes" : "Mes contacts (démonstration)"}</b>
              <button className="ms-back" style={{ width: 30, height: 30 }} data-testid="ms-sheet-close" aria-label="Fermer" onClick={() => setSheet(null)}><X size={15} /></button>
            </div>
            {sheet === "recent" ? (
              recentOrders.length === 0 ? <p className="ms-empty">Aucune recharge récente.</p> : recentOrders.map((o) => (
                <button key={o.id} className="ms-sheet-item" data-testid={`ms-recent-order-${o.id}`} onClick={() => pickRecent(o)}>
                  <BrandLogo brand={(catalog?.operators || []).find((x) => x.id === o.operator_id)?.brand} size={38} />
                  <span style={{ flex: 1 }}><b>{o.offer_name}</b><span>{o.beneficiary_masked} · {api.fmtFcfa(o.total_amount)}</span></span>
                  <span className={`ms-status ${o.status}`}>{o.status === "success" ? "RÉUSSIE" : o.status === "failed" ? "ÉCHEC" : "EN COURS"}</span>
                </button>
              ))
            ) : (
              sheetContacts.map((c) => (
                <button key={c.id} className="ms-sheet-item" data-testid={`ms-sheet-contact-${c.id}`} onClick={() => pickContact(c)}>
                  <InitialsAvatar name={c.name} color={c.color} size={40} />
                  <span style={{ flex: 1 }}><b>{c.name}</b><span>{c.phone.replace(/(\d{2})(?=\d)/g, "$1 ")}</span></span>
                  {c.favorite && <span style={{ color: "#F59E0B", fontSize: 14 }}>★</span>}
                </button>
              ))
            )}
          </div>
        </div>
      )}

      {sheet === "help" && (
        <div className="ms-sheet-mask" data-testid="ms-help-sheet" onClick={() => setSheet(null)}>
          <div className="ms-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="ms-sheet-head"><b>Aide — AYA Multiservice</b>
              <button className="ms-back" style={{ width: 30, height: 30 }} aria-label="Fermer" onClick={() => setSheet(null)}><X size={15} /></button>
            </div>
            <p style={{ fontSize: 12.5, color: "#40564B", lineHeight: 1.6 }}>
              Choisissez un opérateur, un type de recharge, le numéro du bénéficiaire puis un montant ou une offre.
              Sélectionnez ensuite votre moyen de paiement et confirmez.
            </p>
            <div className="ms-demo-note">Version de démonstration : toutes les opérations sont simulées, aucune somme réelle n'est débitée et aucun crédit réel n'est envoyé.</div>
          </div>
        </div>
      )}
    </div>
  );
}
